import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, Download, ExternalLink, Loader2, Plus, Save, Search, Share2, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/sonner";
import { ArticleBlockRenderer } from "@/components/article-blocks/ArticleBlockRenderer";
import { BlockEditor } from "@/components/editor/BlockEditor";
import { blocksFromPlainText, normalizeBlocks } from "@/lib/articleBlocks";

const DEFAULT_CATEGORIES = ["Alles", "Nieuws", "Onderzoek", "Mantelzorg", "Activiteiten", "Vrijwilligers", "Cultuur", "Gemeente", "Impact", "Stichting"];
const EMPTY_ARTICLE = {
  title: "",
  slug: "",
  summary: "",
  body: "",
  body_format: "simple",
  body_blocks: [],
  author: "",
  publish_date: new Date().toISOString().slice(0, 10),
  category: "Nieuws",
  tags: [],
  header_image: "",
  gallery: [],
  seo_title: "",
  seo_description: "",
  canonical: "",
  og_image: "",
  status: "concept",
  featured: false,
  archived: false,
  downloads: [],
  sources: [],
  linked_event_ids: []
};
const EMPTY_IMPACT = { label: "", value: "", description: "", order: 0, published: true };

const formatDate = (value) => {
  if (!value) return "";
  return new Intl.DateTimeFormat("nl-NL", { day: "numeric", month: "long", year: "numeric" }).format(new Date(value));
};

const splitList = (value) => value.split(",").map(item => item.trim()).filter(Boolean);
const joinList = (value) => (value || []).join(", ");
const splitSource = (value = "") => {
  const match = value.match(/^(.*?):\s*(https?:\/\/.+)$/i);
  if (match) return { title: match[1].trim(), url: match[2].trim() };
  if (/^https?:\/\//i.test(value.trim())) return { title: "", url: value.trim() };
  return { title: value.trim(), url: "" };
};
const joinSource = ({ title = "", url = "" }) => {
  const cleanTitle = title.trim();
  const cleanUrl = url.trim();
  if (cleanTitle && cleanUrl) return `${cleanTitle}: ${cleanUrl}`;
  return cleanTitle || cleanUrl;
};
const sourceLabel = (source) => {
  const { title, url } = splitSource(source);
  return title || url || source;
};
const sourceUrl = (source) => {
  const { url } = splitSource(source);
  return url;
};

const ALLOWED_HTML_TAGS = new Set(["a", "article", "aside", "blockquote", "br", "div", "em", "figcaption", "figure", "h1", "h2", "h3", "h4", "h5", "h6", "hr", "i", "img", "li", "ol", "p", "section", "span", "strong", "table", "tbody", "td", "th", "thead", "tr", "u", "ul"]);
const ALLOWED_HTML_ATTRIBUTES = new Set(["alt", "colspan", "href", "rel", "rowspan", "src", "target", "title"]);

const sanitizeHtml = (html) => {
  if (!html || typeof document === "undefined") return "";
  const template = document.createElement("template");
  template.innerHTML = html;

  const cleanNode = (node) => {
    if (node.nodeType === Node.COMMENT_NODE) {
      node.remove();
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;

    const tag = node.tagName.toLowerCase();
    if (!ALLOWED_HTML_TAGS.has(tag)) {
      const parent = node.parentNode;
      while (node.firstChild) parent.insertBefore(node.firstChild, node);
      node.remove();
      return;
    }

    [...node.attributes].forEach((attribute) => {
      const name = attribute.name.toLowerCase();
      const value = (attribute.value || "").trim();
      const hasUnsafeUrl = ["href", "src"].includes(name) && /^(javascript|data):/i.test(value);
      if (name.startsWith("on") || name === "style" || !ALLOWED_HTML_ATTRIBUTES.has(name) || hasUnsafeUrl) {
        node.removeAttribute(attribute.name);
      }
    });

    if (tag === "a") {
      node.setAttribute("rel", "noopener noreferrer");
      if (/^https?:\/\//i.test(node.getAttribute("href") || "")) node.setAttribute("target", "_blank");
    }

    [...node.childNodes].forEach(cleanNode);
  };

  [...template.content.childNodes].forEach(cleanNode);
  return template.innerHTML;
};

const renderBody = (body, format = "simple") => {
  if (format === "html") {
    const cleanHtml = sanitizeHtml(body);
    if (!cleanHtml) return <p className="text-muted-foreground">Dit artikel heeft nog geen inhoud.</p>;
    return <div className="article-rich-content" dangerouslySetInnerHTML={{ __html: cleanHtml }} />;
  }

  const blocks = (body || "").split(/\n{2,}/).map(block => block.trim()).filter(Boolean);
  if (blocks.length === 0) return <p className="text-muted-foreground">Dit artikel heeft nog geen inhoud.</p>;
  return <div className="article-rich-content">{blocks.map((block, index) => {
    if (block.startsWith("### ")) return <h3 key={index} className="mt-8 font-heading text-2xl font-semibold">{block.replace(/^### /, "")}</h3>;
    if (block.startsWith("## ")) return <h2 key={index} className="mt-10 font-heading text-3xl font-semibold">{block.replace(/^## /, "")}</h2>;
    if (block.startsWith("> ")) return <blockquote key={index} className="border-l-4 border-accent pl-5 text-lg italic text-muted-foreground">{block.replace(/^> /, "")}</blockquote>;
    return <p key={index} className="text-lg leading-relaxed text-muted-foreground">{block}</p>;
  })}</div>;
};

const ArticleCard = ({ article, compact = false }) => (
  <Link to={`/kennis-en-nieuws/${article.slug}`} className="group block border-t border-border py-6 transition-colors hover:border-primary">
    {article.header_image && <img src={article.header_image} alt="" loading="lazy" className={`w-full object-cover ${compact ? "aspect-[16/9]" : "aspect-[4/3]"}`} />}
    <div className={article.header_image ? "pt-5" : ""}>
      <p className="section-label">{article.category}</p>
      <h3 className="mt-2 font-heading text-2xl font-semibold leading-tight group-hover:text-primary">{article.title}</h3>
      <p className="mt-3 line-clamp-3 leading-relaxed text-muted-foreground">{article.summary}</p>
      <div className="mt-4 flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
        {article.author && <span>{article.author}</span>}
        <span>{formatDate(article.publish_date)}</span>
        <span>{article.reading_minutes || 1} min lezen</span>
      </div>
      {article.tags?.length > 0 && <div className="mt-4 flex flex-wrap gap-2">{article.tags.slice(0, 4).map(tag => <span key={tag} className="rounded-full bg-secondary px-3 py-1 text-xs font-semibold text-primary">{tag}</span>)}</div>}
    </div>
  </Link>
);

export const KnowledgeHomeBlocks = ({ api, compact = false }) => {
  const [latest, setLatest] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [impact, setImpact] = useState([]);

  useEffect(() => {
    let mounted = true;
    Promise.all([
      axios.get(`${api}/news/latest?limit=3`).then(({ data }) => data.items || []).catch(() => []),
      axios.get(`${api}/news/featured?limit=1`).then(({ data }) => data.items || []).catch(() => []),
      axios.get(`${api}/impact`).then(({ data }) => data.items || []).catch(() => [])
    ]).then(([latestItems, featuredItems, impactItems]) => {
      if (mounted) {
        setLatest(latestItems);
        setFeatured(featuredItems);
        setImpact(impactItems);
      }
    });
    return () => { mounted = false; };
  }, [api]);

  if (latest.length === 0 && featured.length === 0 && impact.length === 0) return null;

  if (compact) {
    const primaryArticle = featured[0] || latest[0];
    return (
      <div className="home-highlight-panel">
        <div className="flex items-start justify-between gap-6">
          <div>
            <p className="section-label">Kennis & Nieuws</p>
            <h2 className="mt-3 font-heading text-3xl font-bold leading-tight md:text-4xl">Laatste ontwikkelingen</h2>
          </div>
          <Button asChild variant="link" className="mt-1 shrink-0 px-0 text-primary"><Link to="/kennis-en-nieuws">Bekijk alles <ArrowRight className="ml-2 h-4 w-4" /></Link></Button>
        </div>
        {primaryArticle ? (
          <Link to={`/kennis-en-nieuws/${primaryArticle.slug}`} className="mt-7 block border-t border-border pt-5 transition-colors hover:border-primary">
            <p className="section-label">{primaryArticle.category}</p>
            <h3 className="mt-2 max-w-xl font-heading text-2xl font-semibold leading-snug text-foreground">{primaryArticle.title}</h3>
            <p className="mt-3 line-clamp-3 max-w-xl text-base leading-relaxed text-muted-foreground">{primaryArticle.summary}</p>
            <div className="mt-4 flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
              {primaryArticle.author && <span>{primaryArticle.author}</span>}
              <span>{formatDate(primaryArticle.publish_date)}</span>
              <span>{primaryArticle.reading_minutes || 1} min lezen</span>
            </div>
          </Link>
        ) : (
          <p className="mt-7 border-t border-border pt-5 text-muted-foreground">Er zijn nog geen gepubliceerde artikelen.</p>
        )}
      </div>
    );
  }

  return (
  <section className="editorial-section bg-secondary/35">
      <div className="editorial-container">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="section-label">Kennis & Nieuws</p>
            <h2 className="section-title">Laatste ontwikkelingen</h2>
          </div>
          <Button asChild variant="link" className="px-0 text-primary"><Link to="/kennis-en-nieuws">Bekijk alles <ArrowRight className="ml-2 h-4 w-4" /></Link></Button>
        </div>
        {impact.length > 0 && <div className="mt-8 grid gap-8 border-y border-border py-7 md:grid-cols-4">
          {impact.slice(0, 4).map(item => <article key={item.id}><p className="font-heading text-4xl font-bold text-primary">{item.value}</p><p className="mt-2 font-semibold">{item.label}</p><p className="mt-1 text-sm text-muted-foreground">{item.description}</p></article>)}
        </div>}
        {featured[0] && <div className="mt-8"><ArticleCard article={featured[0]} compact /></div>}
        {latest.length > 0 && <div className="mt-8 grid gap-5 md:grid-cols-3">{latest.map(article => <ArticleCard key={article.id} article={article} compact />)}</div>}
      </div>
    </section>
  );
};

export const KnowledgeNewsPage = ({ api }) => {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("Alles");
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [tags, setTags] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [popular, setPopular] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${api}/news/categories`).then(({ data }) => setCategories(["Alles", ...(data.items || []).map(item => item.name).filter(Boolean)])).catch(() => setCategories(DEFAULT_CATEGORIES));
    axios.get(`${api}/news/tags`).then(({ data }) => setTags((data.items || []).map(item => item.name).filter(Boolean))).catch(() => setTags([]));
    axios.get(`${api}/news/featured?limit=3`).then(({ data }) => setFeatured(data.items || [])).catch(() => setFeatured([]));
    axios.get(`${api}/news/popular?limit=5`).then(({ data }) => setPopular(data.items || [])).catch(() => setPopular([]));
  }, [api]);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (query) params.set("q", query);
    if (category !== "Alles") params.set("category", category);
    axios.get(`${api}/news?${params.toString()}`).then(({ data }) => setArticles(data.items || [])).catch(() => setArticles([])).finally(() => setLoading(false));
  }, [api, query, category]);

  return (
    <>
      <section className="editorial-section border-b border-border">
        <div className="editorial-container grid items-end gap-12 lg:grid-cols-[minmax(0,1fr)_24rem]">
          <div>
            <p className="section-label">Kenniscentrum</p>
            <h1 className="mt-3 font-heading text-4xl font-bold leading-tight text-primary sm:text-5xl lg:text-6xl">Kennis & Nieuws</h1>
            <p className="editorial-lead mt-6 max-w-3xl">Blijf op de hoogte van ontwikkelingen rondom dementie, mantelzorg, cultuur, activiteiten en Stichting Rumah Kasih.</p>
            <a href="#nieuwste-artikelen" className="mt-8 inline-flex items-center rounded-full bg-primary px-5 py-3 font-semibold text-white">Bekijk nieuwste artikelen <ArrowRight className="ml-2 h-4 w-4" /></a>
          </div>
          <div className="border-l border-border pl-6">
            <p className="text-sm font-bold uppercase text-primary">Voor families, professionals en partners</p>
            <p className="mt-3 leading-relaxed text-muted-foreground">Achtergrond, praktijkupdates en impact rond cultuurgerichte dagbesteding.</p>
          </div>
        </div>
      </section>
      <section id="nieuwste-artikelen" className="editorial-section bg-secondary/35">
        <div className="editorial-container grid gap-10 lg:grid-cols-[minmax(0,1fr)_20rem]">
          <div>
            <div className="border-y border-border py-5">
              <label className="flex items-center gap-3 rounded-md border border-border px-4 py-3">
                <Search className="h-5 w-5 text-muted-foreground" />
                <input value={query} onChange={event => setQuery(event.target.value)} placeholder="Zoek op titel, samenvatting, inhoud of tag" className="w-full bg-transparent outline-none" />
              </label>
              <div className="mt-4 flex flex-wrap gap-2">
                {categories.map(item => <button key={item} type="button" onClick={() => setCategory(item)} className={`rounded-full border px-4 py-2 text-sm font-semibold ${category === item ? "border-primary bg-primary text-white" : "border-border bg-white text-primary"}`}>{item}</button>)}
              </div>
            </div>
            {loading ? <div className="mt-10 flex items-center gap-2 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /> Artikelen laden</div> : (
              <div className="mt-8 grid gap-x-10 md:grid-cols-2">
                {articles.length === 0 ? <p className="text-muted-foreground">Er zijn nog geen gepubliceerde artikelen.</p> : articles.map(article => <ArticleCard key={article.id} article={article} />)}
              </div>
            )}
          </div>
          <aside className="space-y-5">
            <SidebarBlock title="Uitgelicht" articles={featured} />
            <SidebarBlock title="Meest gelezen" articles={popular} />
            <div className="bg-white p-5 shadow-sm"><h2 className="font-heading text-xl font-semibold">Tags</h2><div className="mt-4 flex flex-wrap gap-2">{tags.length === 0 ? <p className="text-sm text-muted-foreground">Nog geen tags.</p> : tags.map(tag => <button key={tag} onClick={() => setQuery(tag)} className="rounded-full bg-secondary px-3 py-1 text-sm text-primary">{tag}</button>)}</div></div>
          </aside>
        </div>
      </section>
    </>
  );
};

const SidebarBlock = ({ title, articles }) => (
  <div className="bg-white p-5 shadow-sm">
    <h2 className="font-heading text-xl font-semibold">{title}</h2>
    <div className="mt-4 space-y-4">
      {articles.length === 0 ? <p className="text-sm text-muted-foreground">Nog geen artikelen.</p> : articles.map(article => <Link key={article.id} to={`/kennis-en-nieuws/${article.slug}`} className="block border-b border-border pb-3 last:border-0 last:pb-0"><p className="text-sm font-semibold text-primary">{article.category}</p><h3 className="mt-1 font-semibold leading-snug">{article.title}</h3></Link>)}
    </div>
  </div>
);

export const KnowledgeArticlePage = ({ api }) => {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${api}/news/${slug}`).then(({ data }) => setData(data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [api, slug]);

  const article = data?.article;
  useEffect(() => {
    if (!article) return;
    document.title = `${article.seo_title || article.title} | Rumah Kasih`;
    const description = document.querySelector("meta[name='description']");
    if (description) description.setAttribute("content", article.seo_description || article.summary || "");
    const existing = document.getElementById("rk-article-schema");
    if (existing) existing.remove();
    const script = document.createElement("script");
    script.id = "rk-article-schema";
    script.type = "application/ld+json";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Article",
      headline: article.title,
      description: article.summary,
      image: article.og_image || article.header_image || undefined,
      author: article.author ? { "@type": "Person", name: article.author } : { "@type": "Organization", name: "Stichting Rumah Kasih" },
      publisher: { "@type": "Organization", name: "Stichting Rumah Kasih" },
      datePublished: article.publish_date,
      dateModified: article.updated_at || article.publish_date,
      mainEntityOfPage: window.location.href
    });
    document.head.appendChild(script);
    return () => script.remove();
  }, [article]);

  if (loading) return <section className="grid min-h-[60vh] place-items-center"><Loader2 className="h-8 w-8 animate-spin text-accent" /></section>;
  if (!article) return <section className="mx-auto max-w-3xl px-4 py-20"><h1 className="section-title">Artikel niet gevonden</h1><Button asChild className="mt-6 rounded-md"><Link to="/kennis-en-nieuws">Terug naar Kennis & Nieuws</Link></Button></section>;

  return (
    <>
      <article>
        {article.header_image && <img src={article.header_image} alt="" className="h-[42vh] w-full object-cover" />}
        <div className="reading-container py-12 md:py-16">
          <p className="section-label">{article.category}</p>
          <h1 className="mt-4 font-heading text-[2.15rem] font-bold leading-[1.08] sm:text-4xl lg:text-5xl">{article.title}</h1>
          <p className="mt-5 text-lg leading-relaxed text-muted-foreground md:text-xl">{article.summary}</p>
          <div className="mt-5 flex flex-wrap gap-4 border-y border-border py-4 text-sm text-muted-foreground">
            {article.author && <span>{article.author}</span>}
            <span>{formatDate(article.publish_date)}</span>
            <span>{article.reading_minutes || 1} min lezen</span>
            <button onClick={() => navigator.share?.({ title: article.title, url: window.location.href })} className="inline-flex items-center gap-1 text-primary"><Share2 className="h-4 w-4" /> Delen</button>
          </div>
          <div className="mt-8">{normalizeBlocks(article.body_blocks).length > 0 ? <ArticleBlockRenderer blocks={article.body_blocks} /> : renderBody(article.body, article.body_format)}</div>
          {(article.sources?.length > 0 || article.downloads?.length > 0) && <div className="mt-10 border-t border-border pt-6">
            {article.sources?.length > 0 && <div><h2 className="font-heading text-2xl font-semibold">Bronvermelding</h2><ul className="mt-3 space-y-2">{article.sources.map(source => <li key={source}>{sourceUrl(source) ? <a href={sourceUrl(source)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-primary underline"><ExternalLink className="h-4 w-4" />{sourceLabel(source)}</a> : <span>{sourceLabel(source)}</span>}</li>)}</ul></div>}
            {article.downloads?.length > 0 && <div className="mt-6"><h2 className="font-heading text-2xl font-semibold">Downloads</h2><ul className="mt-3 space-y-2">{article.downloads.map(download => <li key={download}><a href={download} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-primary underline"><Download className="h-4 w-4" />Download</a></li>)}</ul></div>}
          </div>}
          <aside className="mt-10 border-t border-border pt-6">
            <p className="section-label">Samen verder bouwen</p>
            <div className="mt-3 grid gap-5 md:grid-cols-[minmax(0,1fr)_auto] md:items-end">
              <div>
                <h2 className="font-heading text-2xl font-bold leading-tight md:text-3xl">Help Rumah Kasih zichtbaar en duurzaam worden</h2>
                <p className="mt-3 max-w-2xl leading-relaxed text-muted-foreground">Met steun, samenwerking en vrijwillige inzet kan de dagbesteding blijven groeien voor ouderen, families en mantelzorgers die herkenning en rust nodig hebben.</p>
              </div>
              <div className="flex flex-wrap gap-3">
                <Button asChild className="rounded-full bg-primary px-5 hover:bg-primary/90"><Link to="/steun-ons">Steun de opstart</Link></Button>
                <Button asChild variant="outline" className="rounded-full border-primary px-5 text-primary hover:bg-primary hover:text-white"><Link to="/contact">Samenwerken</Link></Button>
              </div>
            </div>
          </aside>
        </div>
      </article>
      {data.related?.length > 0 && <section className="editorial-section bg-secondary/35"><div className="editorial-container"><h2 className="section-title">Gerelateerde artikelen</h2><div className="mt-8 grid gap-8 md:grid-cols-3">{data.related.map(item => <ArticleCard key={item.id} article={item} compact />)}</div></div></section>}
      <nav className="reading-container flex justify-between gap-4 py-12">
        {data.previous ? <Button asChild variant="outline" className="rounded-md"><Link to={`/kennis-en-nieuws/${data.previous.slug}`}>Vorige</Link></Button> : <span />}
        {data.next ? <Button asChild variant="outline" className="rounded-md"><Link to={`/kennis-en-nieuws/${data.next.slug}`}>Volgende</Link></Button> : <span />}
      </nav>
    </>
  );
};

export const AdminKnowledgeModule = ({ api }) => {
  const [articles, setArticles] = useState([]);
  const [impact, setImpact] = useState([]);
  const [editingArticle, setEditingArticle] = useState(EMPTY_ARTICLE);
  const [editingImpact, setEditingImpact] = useState(EMPTY_IMPACT);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const [newsResponse, impactResponse] = await Promise.all([
      axios.get(`${api}/admin/news`, { withCredentials: true }),
      axios.get(`${api}/admin/impact`, { withCredentials: true })
    ]);
    setArticles(newsResponse.data.items || []);
    setImpact(impactResponse.data.items || []);
  }, [api]);

  useEffect(() => { load().catch(() => {}); }, [load]);

  const saveArticle = async () => {
    setSaving(true);
    const payload = { ...editingArticle, body_format: normalizeBlocks(editingArticle.body_blocks).length > 0 ? "blocks" : editingArticle.body_format, body_blocks: normalizeBlocks(editingArticle.body_blocks), tags: editingArticle.tags || [], gallery: editingArticle.gallery || [], downloads: editingArticle.downloads || [], sources: editingArticle.sources || [] };
    try {
      if (editingArticle.id) await axios.put(`${api}/admin/news/${editingArticle.id}`, payload, { withCredentials: true });
      else await axios.post(`${api}/admin/news`, payload, { withCredentials: true });
      toast.success("Artikel opgeslagen.");
      setEditingArticle(EMPTY_ARTICLE);
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Artikel opslaan lukt niet.");
    } finally {
      setSaving(false);
    }
  };

  const convertBodyToBlocks = () => {
    setEditingArticle(article => ({ ...article, body_blocks: blocksFromPlainText(article.body || ""), body_format: "blocks" }));
  };

  const updateArticleSource = (index, field, value) => {
    const nextSources = [...(editingArticle.sources || [])];
    const source = splitSource(nextSources[index] || "");
    source[field] = value;
    nextSources[index] = joinSource(source);
    setEditingArticle({ ...editingArticle, sources: nextSources });
  };

  const addArticleSource = () => {
    setEditingArticle({ ...editingArticle, sources: [...(editingArticle.sources || []), ""] });
  };

  const removeArticleSource = (index) => {
    setEditingArticle({ ...editingArticle, sources: (editingArticle.sources || []).filter((_, sourceIndex) => sourceIndex !== index) });
  };

  const deleteArticle = async (id) => {
    await axios.delete(`${api}/admin/news/${id}`, { withCredentials: true });
    await load();
  };

  const saveImpact = async () => {
    try {
      if (editingImpact.id) await axios.put(`${api}/admin/impact/${editingImpact.id}`, editingImpact, { withCredentials: true });
      else await axios.post(`${api}/admin/impact`, editingImpact, { withCredentials: true });
      toast.success("Impactcijfer opgeslagen.");
      setEditingImpact(EMPTY_IMPACT);
      await load();
    } catch {
      toast.error("Impactcijfer opslaan lukt niet.");
    }
  };

  return (
    <section className="bg-white p-6 shadow-sm">
      <h2 className="text-2xl font-semibold">Kennis & Nieuws</h2>
      <p className="mt-2 text-sm text-muted-foreground">Beheer artikelen, publicatiestatus en impactcijfers met de block editor. Styling wordt automatisch volgens de Rumah Kasih huisstijl toegepast.</p>
      <div className="mt-6 grid gap-6">
        <div className="grid gap-4 border border-border p-4 sm:grid-cols-2">
          <h3 className="text-lg font-semibold sm:col-span-2">{editingArticle.id ? "Artikel bewerken" : "Nieuw artikel"}</h3>
          <label className="text-sm font-semibold sm:col-span-2">Titel<Input value={editingArticle.title} onChange={e => setEditingArticle({ ...editingArticle, title: e.target.value })} className="mt-2 rounded-md" /></label>
          <label className="text-sm font-semibold">Slug<Input value={editingArticle.slug || ""} onChange={e => setEditingArticle({ ...editingArticle, slug: e.target.value })} className="mt-2 rounded-md" /></label>
          <label className="text-sm font-semibold">Categorie<Input value={editingArticle.category} onChange={e => setEditingArticle({ ...editingArticle, category: e.target.value })} className="mt-2 rounded-md" /></label>
          <label className="text-sm font-semibold sm:col-span-2">Samenvatting<Textarea rows={2} value={editingArticle.summary} onChange={e => setEditingArticle({ ...editingArticle, summary: e.target.value })} className="mt-2 rounded-md" /></label>
          <div className="grid gap-3 sm:col-span-2">
            <div className="rounded-md border border-border bg-secondary/30 p-4">
              <p className="font-semibold">Artikelinhoud</p>
              <p className="mt-1 text-sm leading-relaxed text-muted-foreground">Bouw het artikel op uit blokken. De publieke pagina rendert automatisch veilig en in huisstijl.</p>
              {normalizeBlocks(editingArticle.body_blocks).length === 0 && editingArticle.body && <Button type="button" variant="outline" onClick={convertBodyToBlocks} className="mt-3 rounded-md">Oude tekst omzetten naar blokken</Button>}
            </div>
            <BlockEditor value={editingArticle.body_blocks || []} onChange={blocks => setEditingArticle({ ...editingArticle, body_blocks: blocks, body_format: "blocks" })} />
            <details className="rounded-md border border-border p-4">
              <summary className="cursor-pointer text-sm font-semibold">Legacy tekstveld tonen</summary>
              <p className="mt-2 text-sm text-muted-foreground">Alleen gebruiken om oude inhoud terug te vinden. Nieuwe artikelen worden via blokken opgebouwd.</p>
              <Textarea rows={6} value={editingArticle.body} onChange={e => setEditingArticle({ ...editingArticle, body: e.target.value })} className="mt-3 rounded-md text-sm leading-relaxed" />
            </details>
          </div>
          <label className="text-sm font-semibold">Auteur<Input value={editingArticle.author} onChange={e => setEditingArticle({ ...editingArticle, author: e.target.value })} className="mt-2 rounded-md" /></label>
          <label className="text-sm font-semibold">Publicatiedatum<Input type="date" value={(editingArticle.publish_date || "").slice(0, 10)} onChange={e => setEditingArticle({ ...editingArticle, publish_date: e.target.value })} className="mt-2 rounded-md" /></label>
          <label className="text-sm font-semibold">Status<select value={editingArticle.status} onChange={e => setEditingArticle({ ...editingArticle, status: e.target.value })} className="mt-2 h-10 w-full rounded-md border border-input bg-background px-3"><option value="concept">Concept</option><option value="in_review">In review</option><option value="scheduled">Gepland</option><option value="published">Gepubliceerd</option></select></label>
          <label className="text-sm font-semibold">Tags<Input value={joinList(editingArticle.tags)} onChange={e => setEditingArticle({ ...editingArticle, tags: splitList(e.target.value) })} placeholder="mantelzorg, dementie" className="mt-2 rounded-md" /></label>
          <label className="text-sm font-semibold sm:col-span-2">Headerfoto URL<Input value={editingArticle.header_image} onChange={e => setEditingArticle({ ...editingArticle, header_image: e.target.value })} className="mt-2 rounded-md" /></label>
          <label className="text-sm font-semibold">SEO titel<Input value={editingArticle.seo_title} onChange={e => setEditingArticle({ ...editingArticle, seo_title: e.target.value })} className="mt-2 rounded-md" /></label>
          <label className="text-sm font-semibold">SEO beschrijving<Input value={editingArticle.seo_description} onChange={e => setEditingArticle({ ...editingArticle, seo_description: e.target.value })} className="mt-2 rounded-md" /></label>
          <div className="grid gap-3 sm:col-span-2">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <p className="text-sm font-semibold">Bronnen</p>
                <p className="mt-1 text-sm text-muted-foreground">Voeg per bron een herkenbare naam en de originele URL toe.</p>
              </div>
              <Button type="button" variant="outline" onClick={addArticleSource} className="rounded-md"><Plus className="mr-2 h-4 w-4" />Bron toevoegen</Button>
            </div>
            {(editingArticle.sources || []).length === 0 ? <p className="rounded-md border border-dashed border-border p-4 text-sm text-muted-foreground">Nog geen bronnen toegevoegd.</p> : (editingArticle.sources || []).map((source, index) => {
              const parsedSource = splitSource(source);
              return (
                <div key={index} className="grid gap-3 rounded-md border border-border p-3 md:grid-cols-[1fr_1.4fr_auto]">
                  <label className="text-sm font-semibold">Naam<Input value={parsedSource.title} onChange={e => updateArticleSource(index, "title", e.target.value)} placeholder="Bijv. H&W - Cognitieve achteruitgang" className="mt-2 rounded-md" /></label>
                  <label className="text-sm font-semibold">URL<Input value={parsedSource.url} onChange={e => updateArticleSource(index, "url", e.target.value)} placeholder="https://..." className="mt-2 rounded-md" /></label>
                  <Button type="button" variant="outline" onClick={() => removeArticleSource(index)} className="self-end rounded-md text-destructive"><Trash2 className="h-4 w-4" /></Button>
                </div>
              );
            })}
          </div>
          <label className="text-sm font-semibold sm:col-span-2">Downloads<Input value={joinList(editingArticle.downloads)} onChange={e => setEditingArticle({ ...editingArticle, downloads: splitList(e.target.value) })} className="mt-2 rounded-md" /></label>
          <label className="flex items-center gap-3 text-sm font-semibold"><input type="checkbox" checked={editingArticle.featured} onChange={e => setEditingArticle({ ...editingArticle, featured: e.target.checked })} /> Uitgelicht</label>
          <label className="flex items-center gap-3 text-sm font-semibold"><input type="checkbox" checked={editingArticle.archived} onChange={e => setEditingArticle({ ...editingArticle, archived: e.target.checked })} /> Archief</label>
          <div className="flex gap-3 sm:col-span-2"><Button onClick={saveArticle} disabled={saving} className="rounded-md bg-accent hover:bg-accent/90">{saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}Opslaan</Button><Button variant="outline" onClick={() => setEditingArticle(EMPTY_ARTICLE)} className="rounded-md">Nieuw</Button></div>
        </div>
        <div className="grid gap-3">
          <h3 className="text-lg font-semibold">Artikelen</h3>
          {articles.length === 0 ? <p className="text-muted-foreground">Nog geen artikelen.</p> : articles.map(article => <div key={article.id} className="flex flex-col gap-3 border border-border p-4 sm:flex-row sm:items-center sm:justify-between"><div><p className="font-semibold">{article.title}</p><p className="text-sm text-muted-foreground">{article.category} - {article.status} - {formatDate(article.publish_date)}</p></div><div className="flex gap-2"><Button variant="outline" onClick={() => setEditingArticle(article)} className="rounded-md">Bewerk</Button><Button variant="outline" onClick={() => deleteArticle(article.id)} className="rounded-md text-destructive"><Trash2 className="h-4 w-4" /></Button></div></div>)}
        </div>
        <div className="grid gap-4 border border-border p-4 sm:grid-cols-2">
          <h3 className="text-lg font-semibold sm:col-span-2">Impactcijfers</h3>
          <label className="text-sm font-semibold">Label<Input value={editingImpact.label} onChange={e => setEditingImpact({ ...editingImpact, label: e.target.value })} className="mt-2 rounded-md" /></label>
          <label className="text-sm font-semibold">Waarde<Input value={editingImpact.value} onChange={e => setEditingImpact({ ...editingImpact, value: e.target.value })} className="mt-2 rounded-md" /></label>
          <label className="text-sm font-semibold">Volgorde<Input type="number" value={editingImpact.order} onChange={e => setEditingImpact({ ...editingImpact, order: Number(e.target.value) })} className="mt-2 rounded-md" /></label>
          <label className="flex items-center gap-3 text-sm font-semibold"><input type="checkbox" checked={editingImpact.published} onChange={e => setEditingImpact({ ...editingImpact, published: e.target.checked })} /> Gepubliceerd</label>
          <label className="text-sm font-semibold sm:col-span-2">Omschrijving<Textarea rows={2} value={editingImpact.description} onChange={e => setEditingImpact({ ...editingImpact, description: e.target.value })} className="mt-2 rounded-md" /></label>
          <Button onClick={saveImpact} className="rounded-md bg-accent hover:bg-accent/90"><Plus className="mr-2 h-4 w-4" />Impact opslaan</Button>
          <div className="space-y-2 sm:col-span-2">{impact.map(item => <button key={item.id} type="button" onClick={() => setEditingImpact(item)} className="block w-full border border-border p-3 text-left"><strong>{item.value}</strong> {item.label}</button>)}</div>
        </div>
      </div>
    </section>
  );
};
